export interface Organization {
    id: number;
    uid: string;
    name: string;
}

// Admins administer every location of the organization; installers and
// guests only get a permanent invitation to each one.
export type OrganizationMemberRole = "admin" | "installer" | "guest";

export const ORGANIZATION_MEMBER_ROLES: OrganizationMemberRole[] = [
    "admin",
    "installer",
    "guest",
];

export interface OrganizationMember {
    uid: string;
    email: string;
    name?: string;
    role: OrganizationMemberRole;
}

export interface OrganizationLocationRef {
    uid: string;
    name: string;
}

// Defaults inherited by every location linked to the organization. The API
// uses the same field names and validation as /location/update; only the
// scope changes from loc_uid to organization_uid.
export interface OrganizationLocationSettings {
    invitationLinkAddToHome: boolean;
    invitationLinkAppName: string;
    invitationLinkLocationCheckMode: "off" | "warn" | "required";
    invitationLinkLocationRadius: number;
    invitationLinkHideHeader: boolean;
    invitationLinkHideFooter: boolean;
    urlRecommendations: string;
    videoName?: string;
    videoUrl?: string;
    externalVideoUrl: string;
    earlyCheckinEnabled: boolean;
    earlyCheckoutEnabled: boolean;
    protectAutoEnabledWithoutReservations: boolean;
    enableProtectGuest: boolean;
    securityAlertTime: number;
    embedDomains: Array<{
        id: number;
        domain: string;
        is_active: boolean;
        note: string;
    }>;
}

export interface OrganizationDetail extends Organization {
    ownerUid: string | null;
    // Multi-user organizations are Premium-gated: false while the
    // organization has no location on the Premium rental plan.
    canAddMembers: boolean;
    users: OrganizationMember[];
    locations: OrganizationLocationRef[];
    locationSettings?: OrganizationLocationSettings;
}

export function makeOrganizationLocationSettings(
    settings: Partial<OrganizationLocationSettings> = {},
): OrganizationLocationSettings {
    return {
        invitationLinkAddToHome: false,
        invitationLinkAppName: "",
        invitationLinkLocationCheckMode: "warn",
        invitationLinkLocationRadius: 200,
        invitationLinkHideHeader: false,
        invitationLinkHideFooter: false,
        urlRecommendations: "",
        externalVideoUrl: "",
        earlyCheckinEnabled: false,
        earlyCheckoutEnabled: false,
        protectAutoEnabledWithoutReservations: false,
        enableProtectGuest: false,
        securityAlertTime: 0,
        embedDomains: [],
        ...settings,
    };
}

export function makeOrganization({
    id,
    uid,
    name,
}: Partial<Organization> = {}): Organization {
    return {
        id: id as number,
        uid: uid as string,
        name: name as string,
    };
}
